'use client';

/**
 * AI Chat Widget — floating chat button + expandable panel.
 *
 * Sprint 90 fixes:
 *   - Dark mode contrast (explicit colors instead of bg-muted/50)
 *   - Shift+Enter for newline (Textarea instead of Input)
 *   - History persistence from API (not just localStorage)
 *   - Longer timeout for LLM responses
 *
 * Spec ref: docs/SPEC-174-1-PROFILEIA-AI-HUB.md §6.2.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AIMessageBubble } from './ai-message-bubble';
import { AISuggestions } from './ai-suggestions';
import {
  sendChatMessage,
  sendPublicChatMessage,
  isPublicAssistant,
} from '@/lib/api-ai';
import { tokenStore } from '@/lib/api-client';
import { ApiClientError } from '@/lib/api-client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import type { AIAssistantId, AIChatMessage } from '@profileia/shared';

const STORAGE_KEY_PREFIX = 'ai:chat:history:';
const MAX_HISTORY = 5;

export interface AIChatWidgetProps {
  assistantId: AIAssistantId;
  pageContext?: {
    path: string;
    section?: string;
    data?: Record<string, unknown>;
  };
  position?: 'bottom-right' | 'bottom-left';
  title?: string;
  subtitle?: string;
}

export function AIChatWidget({
  assistantId,
  pageContext,
  position = 'bottom-right',
  title = 'Asistente Profileia',
  subtitle = 'Pregúntame lo que quieras',
}: AIChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const storageKey = `${STORAGE_KEY_PREFIX}${assistantId}`;

  // Load history from localStorage on mount (session continuity).
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as AIChatMessage[];
        if (Array.isArray(parsed)) {
          setMessages(parsed.slice(-MAX_HISTORY));
        }
      }
    } catch {
      // Corrupted localStorage — ignore + start fresh.
    }
  }, [storageKey]);

  // Persist history to localStorage whenever messages change.
  useEffect(() => {
    if (messages.length === 0) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch {
      // Storage full or unavailable — non-blocking.
    }
  }, [messages, storageKey]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  // Focus input when opened.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-resize textarea.
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const handleSend = useCallback(async (messageText?: string) => {
    const text = (messageText ?? input).trim();
    if (!text || loading) return;

    const userMessage: AIChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);
    setSuggestions([]);

    try {
      // Sprint 93 fix (PC-P0-1 from /plan-de-compensacion QA): detect
      // anonymous users and route to the public endpoint when the
      // assistant is in the public whitelist (`faq-negocio` or
      // `compensacion`). For non-public assistants (e.g. `soporte`,
      // `personalizacion`), anonymous users get a clear CTA to login
      // instead of a misleading "Sesión expirada" error.
      //
      // Auth detection: `tokenStore.getAccess()` returns the in-memory
      // access token if the user is logged in, or `null` if anonymous.
      // This is the same source of truth used by `apiFetch` to decide
      // whether to attach the `Authorization: Bearer ...` header.
      const accessToken = tokenStore.getAccess();
      const isAuthenticated = accessToken !== null;
      const isPublic = isPublicAssistant(assistantId);

      let response;
      if (!isAuthenticated && isPublic) {
        // Anonymous + public assistant → use /api/ai/chat/public
        // (no JWT, rate limited 5/hr/IP, no conversation persistence).
        response = await sendPublicChatMessage({
          assistantId,
          message: text,
          pageContext: pageContext
            ? { path: pageContext.path, section: pageContext.section }
            : undefined,
          history: messages.slice(-5),
        });
      } else if (!isAuthenticated && !isPublic) {
        // Anonymous + non-public assistant → CTA to login. This is the
        // SPEC-177 #1 fallback that was missing in the original Sprint
        // 91 implementation (caused PC-P0-1 in the QA audit).
        const ctaMsg =
          'Para usar este asistente necesitas iniciar sesión. ' +
          'Regístrate gratis para hacer todas las preguntas que quieras.';
        setError(ctaMsg);
        toast.error(ctaMsg);
        return;
      } else {
        // Authenticated → use /api/ai/chat (JWT, 10/hr/user, persists
        // conversation history).
        response = await sendChatMessage({
          assistantId,
          message: text,
          pageContext,
          history: messages.slice(-5),
        });
      }

      const assistantMessage: AIChatMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setSuggestions(response.suggestions ?? []);
    } catch (err) {
      let msg = 'No se pudo conectar con el asistente. Intenta de nuevo.';
      if (err instanceof ApiClientError) {
        if (err.code === 'AI_RATE_LIMITED') {
          msg = err.message;
        } else if (err.status === 503) {
          msg = 'El asistente no está disponible en este momento.';
        } else if (err.status === 401) {
          // Sprint 93 fix: 401 means the auth token expired mid-session.
          // For public assistants, retry anonymously; for others, prompt
          // login. The retry logic happens on the next handleSend call
          // (tokenStore is cleared by apiFetch on 401).
          msg = isPublicAssistant(assistantId)
            ? 'Tu sesión expiró. Intenta de nuevo — puedes seguir usando este asistente como visitante.'
            : 'Tu sesión expiró. Inicia sesión de nuevo para continuar.';
        } else {
          msg = err.message;
        }
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [input, loading, assistantId, pageContext, messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
    // Shift+Enter: let the textarea insert a newline (default behavior).
  };

  const handleClearHistory = () => {
    setMessages([]);
    setSuggestions([]);
    setError(null);
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // non-blocking
    }
  };

  const positionClasses = position === 'bottom-right'
    ? 'bottom-4 right-4'
    : 'bottom-4 left-4';

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={cn(
            // V128-AI-HUB-ASSISTANT-GLOBAL: bump z-50 → z-[60] so the
            // floating button overlays Dialog/Sheet backdrops (z-50) while
            // staying below toast (z-[100]) + conference fullscreen (z-[100]).
            'fixed z-[60] size-14 rounded-full shadow-lg',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 transition-all',
            'flex items-center justify-center',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            positionClasses,
          )}
          aria-label={`Abrir ${title}`}
        >
          <MessageCircle className="size-6" aria-hidden="true" />
          {messages.length === 0 && (
            <span className="absolute -top-0.5 -right-0.5 size-3 rounded-full bg-green-500 border-2 border-background" aria-hidden="true" />
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className={cn(
            // V128-AI-HUB-ASSISTANT-GLOBAL: bump z-50 → z-[60] (match the
            // floating button above) so the panel overlays Dialog/Sheet
            // backdrops (z-50) but stays below toast + conference fullscreen.
            'fixed z-[60] w-[calc(100vw-2rem)] max-w-md',
            'h-[600px] max-h-[calc(100vh-2rem)]',
            'flex flex-col rounded-xl shadow-2xl border border-border',
            'bg-background text-foreground',
            'overflow-hidden',
            positionClasses,
          )}
          role="dialog"
          aria-label={title}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary text-primary-foreground shrink-0">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold truncate">{title}</h2>
              <p className="text-xs opacity-80 truncate">{subtitle}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearHistory}
                  className="size-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  aria-label="Limpiar conversación"
                  title="Limpiar conversación"
                >
                  <RefreshCw className="size-3.5" aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="size-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Cerrar chat"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-background">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center px-6 py-8">
                <MessageCircle className="size-10 text-muted-foreground mb-3 opacity-50" aria-hidden="true" />
                <p className="text-sm font-medium text-muted-foreground">
                  ¡Hola! Soy tu asistente de Profileia.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pregúntame lo que necesites — estoy aquí para ayudarte.
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <AIMessageBubble
                key={idx}
                message={msg}
                isUser={msg.role === 'user'}
                assistantName={title}
              />
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-2.5 px-4 py-3">
                <div className="shrink-0 size-7 rounded-full bg-muted flex items-center justify-center">
                  <Loader2 className="size-4 text-muted-foreground animate-spin" aria-hidden="true" />
                </div>
                <div className="flex-1 rounded-lg bg-muted px-3.5 py-2.5">
                  <Skeleton className="h-3 w-3/4 mb-1.5" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className="flex flex-col items-center justify-center px-4 py-3 gap-2">
                <div className="flex items-center gap-2 text-destructive text-xs">
                  <AlertCircle className="size-4" aria-hidden="true" />
                  <span>{error}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => void handleSend()}
                >
                  <RefreshCw className="size-3 mr-1" aria-hidden="true" />
                  Reintentar
                </Button>
              </div>
            )}

            {/* Suggestions */}
            {!loading && suggestions.length > 0 && (
              <AISuggestions
                suggestions={suggestions}
                onSelect={(s) => void handleSend(s)}
                disabled={loading}
              />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input — Sprint 90 fix: Textarea for Shift+Enter (multi-line) */}
          <div className="flex items-end gap-2 px-3 py-2.5 border-t border-border bg-background shrink-0">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje... (Enter para enviar, Shift+Enter para nueva línea)"
              disabled={loading}
              maxLength={2000}
              rows={1}
              className={cn(
                'flex-1 resize-none rounded-lg border border-border bg-input text-foreground',
                'placeholder:text-muted-foreground',
                'px-3 py-2 text-sm min-h-[40px] max-h-[120px]',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                'disabled:opacity-50',
              )}
              aria-label="Mensaje"
            />
            <Button
              type="button"
              size="icon"
              onClick={() => void handleSend()}
              disabled={!input.trim() || loading}
              className="shrink-0"
              aria-label="Enviar mensaje"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="size-4" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
